<!--
Copyright 2020 ODK Central Developers
See the NOTICE file at the top-level directory of this distribution and at
https://github.com/getodk/central-frontend/blob/master/NOTICE.

This file is part of ODK Central. It is subject to the license terms in
the LICENSE file found in the top-level directory of this distribution and at
https://www.apache.org/licenses/LICENSE-2.0. No part of ODK Central,
including this file, may be copied, modified, propagated, or distributed
except according to the terms contained in the LICENSE file.
-->
<template>
  <modal id="public-link-create" :state="state" :hideable="!awaitingResponse"
    backdrop @hide="$emit('hide')" @shown="$refs.displayName.focus()">
    <template #title>{{ $t('title') }}</template>
    <template #body>
      <p class="modal-introduction">{{ $t('introduction[0]') }}</p>
      <form @submit.prevent="submit">
        <form-group ref="displayName" v-model.trim="displayName"
          :placeholder="$t('field.displayName')" required autocomplete="off"/>
        <div class="checkbox">
          <label>
            <input v-model="once" type="checkbox"
              aria-describedby="public-link-create-once-help">
              {{ $t('field.once') }}
          </label>
          <p id="public-link-create-once-help" class="help-block">
            {{ $t('onceHelp') }}
            <doc-link to="central-submissions/#public-access-links">{{ $t('moreInfo.learnMore') }}</doc-link>
          </p>
        </div>
        <div class="modal-actions">
          <button type="submit" class="btn btn-primary"
            :disabled="awaitingResponse">
            {{ $t('action.create') }} <spinner :state="awaitingResponse"/>
          </button>
          <button type="button" class="btn btn-link"
            :disabled="awaitingResponse" @click="$emit('hide')">
            {{ $t('action.cancel') }}
          </button>
        </div>
      </form>
    </template>
  </modal>
</template>

<script>
import DocLink from '../doc-link.vue';
import FormGroup from '../form-group.vue';
import Modal from '../modal.vue';
import Spinner from '../spinner.vue';
import request from '../../mixins/request';
import { apiPaths } from '../../util/request';
import { noop } from '../../util/util';
import { requestData } from '../../store/modules/request';

export default {
  name: 'PublicLinkCreate',
  components: { DocLink, FormGroup, Modal, Spinner },
  mixins: [request()],
  props: {
    state: {
      type: Boolean,
      default: false
    }
  },
  data() {
    return {
      awaitingResponse: false,
      displayName: '',
      once: false
    };
  },
  // The modal assumes that this data will exist when the modal is shown.
  computed: requestData(['form']),
  watch: {
    state(state) {
      if (!state) {
        this.displayName = '';
        this.once = false;
      }
    }
  },
  methods: {
    submit() {
      this.request({
        method: 'POST',
        url: apiPaths.publicLinks(this.form.projectId, this.form.xmlFormId),
        data: { displayName: this.displayName, once: this.once }
      })
        .then(({ data }) => {
          this.$emit('success', data);
        })
        .catch(noop);
    }
  }
};
</script>

<i18n lang="json5">
{
  "en": {
    // This is the title at the top of a pop-up.
    "title": "Create Public Access Link",
    "introduction": [
      "Anyone with this Link will be able to fill out this Form in a web browser. Use the display name to remind yourself of where you posted it, who you shared it with, when it is intended to be active, and so on."
    ],
    "field": {
      "once": "Single Submission"
    },
    // This is help text for the "Single Submission" checkbox.
    "onceHelp": "Allow only one Submission from each browser."
  }
}
</i18n>

<!-- Autogenerated by destructure.js -->
<i18n>
{
  "cs": {
    "title": "Vytvořit veřejně přístupný odkaz",
    "introduction": [
      "Kdokoli s tímto odkazem bude moci vyplnit tento formulář ve webovém prohlížeči. Zobrazovaný název používejte, abyste si připomněli, kde jste ho zveřejnili, s kým jste jej sdíleli, kdy má být aktivní atd."
    ]
  },
  "de": {
    "title": "Öffentlichen Zugangslink erstellen",
    "introduction": [
      "Jeder mit diesem Link kann das Formular mit einem Webbrowser ausfüllen. Verwenden Sie den Anzeigenamen, um sich zu erinnern, wo Sie ihn veröffentlicht haben, mit wem Sie ihn geteilt haben, wann er aktiv sein soll und so weiter."
    ],
    "field": {
      "once": "Einzelübermittlung"
    },
    "onceHelp": "Erlaubt nur eine Übermittlung von jedem Browser."
  },
  "es": {
    "title": "Crear enlace de acceso público",
    "introduction": [
      "Cualquier persona con este enlace será capaz de llenar este formulario desde un navegador web. Utilice nombre para mostrar para recordar dónde lo ha publicado, con quién lo ha compartido, hasta cuando pretende que esté activo, entre otras cosas."
    ],
    "field": {
      "once": "Envío único"
    },
    "onceHelp": "Permitir solo un envío por cada navegador."
  },
  "fr": {
    "title": "Créer lien d'accès public",
    "introduction": [
      "N'importe qui avec ce lien pourra remplir ce formulaire depuis leur navigateur. Utilisez le nom affiché pour vous souvenir d'où vous l'avez publié, avec qui vous l'avez partagé, quand il est destiné à être actif et ainsi de suite."
    ],
    "field": {
      "once": "Soumission unique"
    },
    "onceHelp": "N'accepter qu'une soumission par répondant."
  }
}
</i18n>
